/**
 * <s-proof-globe data-land="…json" data-home="lon,lat">: a dotted 3D globe on canvas (Shopify order-globe
 * style). Land dots come from a precomputed list (globe-land.json, lon/lat × 10). Coral arcs fly from cities
 * around the world into "home" (the studio), each landing with a pulse. The globe sways gently around home and
 * can be dragged with a mouse (inertia). Pauses off screen; reduced motion draws one still frame with arcs.
 */
const RAD = Math.PI / 180;
const CITIES = [
  [-74, 40.7], [-118.2, 34], [-87.6, 41.9], [-79.4, 43.7], [-99.1, 19.4], [-123.1, 49.3], [-46.6, -23.5],
  [-58.4, -34.6], [-0.1, 51.5], [2.35, 48.86], [13.4, 52.5], [-3.7, 40.4], [12.5, 41.9], [4.9, 52.4],
  [18.1, 59.3], [23.3, 42.7], [34.8, 32.1], [55.3, 25.2], [46.7, 24.7], [72.9, 19.1], [77.2, 28.6],
  [103.8, 1.35], [106.8, -6.2], [151.2, -33.9], [145, -37.8], [139.7, 35.7], [127, 37.6], [18.4, -33.9],
  [3.4, 6.5], [29, 41], [174.8, -36.8], [100.5, 13.75], [121, 14.6], [-80.2, 25.8], [8.5, 47.4], [37.6, 55.75],
];

class SProofGlobe extends HTMLElement {
  #canvas = null;
  #ctx = null;
  #land = null;
  #home = [105.85, 21.59];
  #w = 0;
  #h = 0;
  #dpr = 1;
  #raf = 0;
  #running = false;
  #visible = false;
  #reduced = false;
  #arcs = [];
  #zoom = 0;
  #pulses = [];
  #lastSpawn = 0;
  #t0 = 0;
  #drag = null;
  #offset = 0;
  #velocity = 0;
  #io = null;
  #ro = null;
  #ac = null;

  connectedCallback() {
    this.#canvas = this.querySelector('canvas');
    if (!this.#canvas) return;
    this.#ctx = this.#canvas.getContext('2d');
    const home = (this.dataset.home || '').split(',').map(Number);
    if (home.length === 2 && home.every(Number.isFinite)) this.#home = home;
    this.#reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.#ac = new AbortController();

    this.#ro = new ResizeObserver(() => this.#resize());
    this.#ro.observe(this);
    this.#io = new IntersectionObserver(([entry]) => {
      this.#visible = entry.isIntersecting;
      if (this.#visible) this.#load();
      this.#sync();
    }, { rootMargin: '200px 0px' });
    this.#io.observe(this);
    document.addEventListener('visibilitychange', () => this.#sync(), { signal: this.#ac.signal });
    if (!this.#reduced && window.matchMedia('(hover: hover) and (pointer: fine)').matches) this.#bindDrag();
  }

  disconnectedCallback() {
    this.#io?.disconnect();
    this.#ro?.disconnect();
    this.#ac?.abort();
    cancelAnimationFrame(this.#raf);
    this.#running = false;
  }

  async #load() {
    if (this.#land || !this.dataset.land) return;
    this.#land = [];
    try {
      const response = await fetch(this.dataset.land);
      const json = await response.json();
      const d = json.d || [];
      const pts = new Float32Array(d.length);
      for (let i = 0; i < d.length; i += 2) {
        pts[i] = (d[i] / 10) * RAD;
        pts[i + 1] = (d[i + 1] / 10) * RAD;
      }
      this.#land = pts;
      this.setAttribute('data-ready', '');
    } catch {
      this.#land = [];
    }
    if (this.#reduced) this.#still();
    this.#sync();
  }

  #resize() {
    const rect = this.getBoundingClientRect();
    this.#dpr = Math.min(window.devicePixelRatio || 1, 2);
    this.#w = rect.width;
    this.#h = rect.height;
    this.#canvas.width = Math.round(rect.width * this.#dpr);
    this.#canvas.height = Math.round(rect.height * this.#dpr);
    if (this.#reduced) this.#still();
  }

  #sync() {
    const should = this.#visible && !document.hidden && !this.#reduced && this.#land;
    if (should && !this.#running) {
      this.#running = true;
      this.#t0 = this.#t0 || performance.now();
      this.#raf = requestAnimationFrame((t) => this.#frame(t));
    } else if (!should && this.#running) {
      this.#running = false;
      cancelAnimationFrame(this.#raf);
    }
  }

  #bindDrag() {
    const opts = { signal: this.#ac.signal };
    this.addEventListener('pointerdown', (event) => {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      this.#drag = { x: event.clientX, last: event.clientX, time: performance.now() };
      this.setAttribute('data-dragging', '');
      this.setPointerCapture(event.pointerId);
    }, opts);
    this.addEventListener('pointermove', (event) => {
      if (!this.#drag) return;
      const now = performance.now();
      const dx = event.clientX - this.#drag.last;
      const radius = this.#radius();
      const delta = (dx / radius) * 0.9;
      this.#offset += delta;
      this.#velocity = delta / Math.max(1, now - this.#drag.time) * 16;
      this.#drag.last = event.clientX;
      this.#drag.time = now;
    }, opts);
    const end = () => {
      this.#drag = null;
      this.removeAttribute('data-dragging');
    };
    this.addEventListener('pointerup', end, opts);
    this.addEventListener('pointercancel', end, opts);
  }

  #radius() {
    if (this.dataset.layout === 'horizon') {
      const near = Math.min(this.#w * 0.62, this.#h * 1.25);
      const far = Math.min(this.#w * 0.3, this.#h * 0.38);
      return near + (far - near) * this.#zoom;
    }
    return Math.min(this.#w * (this.#w > 900 ? 0.3 : 0.44), this.#h * 0.46);
  }

  #center() {
    // Horizon layout (closing section): a planet rising from the bottom edge, only its top cap shows.
    if (this.dataset.layout === 'horizon') return [this.#w * 0.5, this.#h * 0.5 + this.#radius() * (1 - this.#zoom)];
    const wide = this.#w > 520;
    return [this.#w * (this.#w > 900 ? 0.68 : wide ? 0.58 : 0.5), this.#h * 0.5];
  }

  /** Rotation state for time t (seconds). */
  #view(t) {
    const sway = this.#reduced ? 0 : Math.sin(t * 0.12) * 0.42;
    const horizon = this.dataset.layout === 'horizon';
    const tilt = horizon ? -38 + 50 * this.#zoom : 18;
    return { lon0: this.#home[0] * RAD - (horizon ? 0 : 0.35) + sway + this.#offset, tilt: tilt * RAD };
  }

  #project(lon, lat, lift, view) {
    const cl = Math.cos(lat);
    const x = cl * Math.sin(lon - view.lon0);
    const y = Math.sin(lat);
    const z = cl * Math.cos(lon - view.lon0);
    const ct = Math.cos(view.tilt);
    const st = Math.sin(view.tilt);
    const y2 = y * ct - z * st;
    const z2 = y * st + z * ct;
    return [x * lift, y2 * lift, z2 * lift];
  }

  /**
   * Public: fly one highlighted arc into home from the visible city closest to a screen x (the visitor's
   * pointer), so it reads as "your store" launching. Fires globe:landed when it lands.
   */
  launchFrom(clientX) {
    if (!this.#land || this.#reduced) return;
    const now = performance.now();
    const view = this.#view((now - this.#t0) / 1000);
    const R = this.#radius();
    const [cx] = this.#center();
    const x = clientX - this.getBoundingClientRect().left;
    let best = null;
    let bestD = Infinity;
    for (const city of CITIES) {
      const [px, , pz] = this.#project(city[0] * RAD, city[1] * RAD, 1, view);
      if (pz <= 0.15) continue;
      const d = Math.abs(cx + px * R - x);
      if (d < bestD && Math.abs(city[0] - this.#home[0]) > 12) { bestD = d; best = city; }
    }
    if (!best) best = CITIES[0];
    this.#arcs.push({ from: best, start: now, duration: 1500, height: 0.34, mine: true });
  }

  #spawn(now) {
    const from = CITIES[Math.floor(Math.random() * CITIES.length)];
    const duration = 1600 + Math.random() * 1100;
    this.#arcs.push({ from, start: now, duration, height: 0.18 + Math.random() * 0.16 });
  }

  /** Point along the great circle from `from` to home at fraction f, as [lon, lat]. */
  #along(from, f) {
    const [lon1, lat1] = [from[0] * RAD, from[1] * RAD];
    const [lon2, lat2] = [this.#home[0] * RAD, this.#home[1] * RAD];
    const a = [Math.cos(lat1) * Math.cos(lon1), Math.cos(lat1) * Math.sin(lon1), Math.sin(lat1)];
    const b = [Math.cos(lat2) * Math.cos(lon2), Math.cos(lat2) * Math.sin(lon2), Math.sin(lat2)];
    const dot = Math.min(1, Math.max(-1, a[0] * b[0] + a[1] * b[1] + a[2] * b[2]));
    const omega = Math.acos(dot);
    if (omega < 1e-4) return [lon2, lat2, 0];
    const s = Math.sin(omega);
    const k1 = Math.sin((1 - f) * omega) / s;
    const k2 = Math.sin(f * omega) / s;
    const p = [a[0] * k1 + b[0] * k2, a[1] * k1 + b[1] * k2, a[2] * k1 + b[2] * k2];
    return [Math.atan2(p[1], p[0]), Math.asin(p[2]), omega];
  }

  /**
   * Horizon layout: scrolling down zooms out from the rising planet cap (0) to the whole globe (1). Target
   * comes from the stage position; the value eases toward it each frame.
   */
  #updateZoom() {
    if (this.dataset.layout !== 'horizon') return;
    const rect = this.getBoundingClientRect();
    const vh = window.innerHeight || 1;
    const target = Math.min(1, Math.max(0, (vh - rect.top - rect.height * 0.3) / (rect.height * 0.9)));
    this.#zoom += (target - this.#zoom) * 0.1;
    if (Math.abs(target - this.#zoom) < 0.001) this.#zoom = target;
  }

  #frame(now) {
    if (!this.#running) return;
    this.#updateZoom();
    const t = (now - this.#t0) / 1000;
    if (!this.#drag) {
      this.#offset += this.#velocity;
      this.#velocity *= 0.94;
      if (Math.abs(this.#velocity) < 0.0001) this.#velocity = 0;
    }
    if (now - this.#lastSpawn > 260 && this.#arcs.length < 16) {
      this.#spawn(now);
      this.#lastSpawn = now;
    }
    this.#draw(now, this.#view(t));
    this.#raf = requestAnimationFrame((next) => this.#frame(next));
  }

  #still() {
    if (!this.#land || !this.#w) return;
    const now = performance.now();
    this.#arcs = CITIES.filter((_, i) => i % 4 === 0).map((from) => ({ from, start: now - 1100, duration: 1000, height: 0.24, still: true }));
    this.#pulses = [];
    this.#draw(now, this.#view(0));
  }

  #draw(now, view) {
    const ctx = this.#ctx;
    const dpr = this.#dpr;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, this.#w, this.#h);
    const R = this.#radius();
    const [cx, cy] = this.#center();

    // atmosphere + sphere
    const glow = ctx.createRadialGradient(cx, cy, R * 0.85, cx, cy, R * 1.35);
    glow.addColorStop(0, 'rgba(255,132,120,0.22)');
    glow.addColorStop(1, 'rgba(255,132,120,0)');
    ctx.fillStyle = glow;
    ctx.beginPath();
    ctx.arc(cx, cy, R * 1.35, 0, Math.PI * 2);
    ctx.fill();
    const body = ctx.createRadialGradient(cx - R * 0.35, cy - R * 0.4, R * 0.1, cx, cy, R);
    body.addColorStop(0, '#26262c');
    body.addColorStop(1, '#141418');
    ctx.fillStyle = body;
    ctx.beginPath();
    ctx.arc(cx, cy, R, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = 'rgba(255,165,155,0.28)';
    ctx.lineWidth = 1;
    ctx.stroke();

    // land dots
    const land = this.#land;
    const size = Math.max(1.1, R / 150);
    for (let i = 0; i < land.length; i += 2) {
      const [x, y, z] = this.#project(land[i], land[i + 1], 1, view);
      if (z <= 0.02) continue;
      ctx.fillStyle = `rgba(255,255,255,${(0.12 + z * 0.62).toFixed(3)})`;
      ctx.fillRect(cx + x * R - size / 2, cy - y * R - size / 2, size, size);
    }

    // arcs
    const kept = [];
    for (const arc of this.#arcs) {
      const p = (now - arc.start) / arc.duration;
      if (p > 1.45) continue;
      kept.push(arc);
      const head = Math.min(1, p);
      const tail = arc.still ? 0 : Math.max(0, p - 0.42);
      const steps = 28;
      let prev = null;
      for (let s = 0; s <= steps; s++) {
        const f = tail + (head - tail) * (s / steps);
        const [lon, lat, omega] = this.#along(arc.from, f);
        const lift = 1 + Math.sin(Math.PI * f) * arc.height * Math.min(1, omega / 1.2 + 0.25);
        const [x, y, z] = this.#project(lon, lat, lift, view);
        const sx = cx + x * R;
        const sy = cy - y * R;
        const shown = z > 0 || x * x + y * y > 1;
        if (prev && shown && prev.shown) {
          const alpha = arc.still ? 0.55 : (s / steps) * (p > 1 ? Math.max(0, 1 - (p - 1) / 0.45) : 1);
          ctx.strokeStyle = `rgba(255,${Math.round(165 - 50 * (s / steps))},${Math.round(155 - 60 * (s / steps))},${alpha.toFixed(3)})`;
          ctx.lineWidth = (arc.mine ? 2.2 : 1) + (s / steps) * (arc.mine ? 2.2 : 1.4);
          ctx.beginPath();
          ctx.moveTo(prev.x, prev.y);
          ctx.lineTo(sx, sy);
          ctx.stroke();
        }
        prev = { x: sx, y: sy, shown };
      }
      if (!arc.still && p < 1 && prev?.shown) {
        ctx.fillStyle = '#fff';
        ctx.beginPath();
        ctx.arc(prev.x, prev.y, arc.mine ? 4 : 2, 0, Math.PI * 2);
        ctx.fill();
      }
      if (!arc.landed && p >= 1) {
        arc.landed = true;
        if (arc.mine) this.dispatchEvent(new CustomEvent('globe:landed', { bubbles: true }));
        this.#pulses.push(now);
      }
    }
    this.#arcs = kept;

    // home marker + landing pulses
    const [hx, hy, hz] = this.#project(this.#home[0] * RAD, this.#home[1] * RAD, 1, view);
    if (hz > 0) {
      const px = cx + hx * R;
      const py = cy - hy * R;
      this.#pulses = this.#pulses.filter((start) => now - start < 900);
      for (const start of this.#pulses) {
        const q = (now - start) / 900;
        ctx.strokeStyle = `rgba(255,132,120,${(0.8 * (1 - q)).toFixed(3)})`;
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.arc(px, py, 4 + q * 22, 0, Math.PI * 2);
        ctx.stroke();
      }
      const halo = ctx.createRadialGradient(px, py, 0, px, py, 16);
      halo.addColorStop(0, 'rgba(255,132,120,0.75)');
      halo.addColorStop(1, 'rgba(255,132,120,0)');
      ctx.fillStyle = halo;
      ctx.beginPath();
      ctx.arc(px, py, 16, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = '#fff';
      ctx.beginPath();
      ctx.arc(px, py, 3.5, 0, Math.PI * 2);
      ctx.fill();
      const label = this.dataset.homeLabel;
      if (label) {
        ctx.font = '12px "Instrument Sans", system-ui, sans-serif';
        ctx.fillStyle = 'rgba(255,255,255,0.9)';
        ctx.fillText(label, px + 10, py - 8);
      }
    }
  }
}

if (!customElements.get('s-proof-globe')) customElements.define('s-proof-globe', SProofGlobe);
